import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, API, fileUrl, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PostcardPreview } from "@/components/PostcardPreview";
import { toast } from "sonner";
import {
  Gauge, Megaphone, Users, GearSix, SignOut, ArrowClockwise, DownloadSimple,
  CurrencyDollar, Ticket, Eye, Plus, Trash, PencilSimple, MapPin, ChartBar, SquaresFour,
  UsersThree, DotsSixVertical, Image as ImageIcon, Clock,
} from "@phosphor-icons/react";

export default function AdminDashboard() {
  const { user, checking, logout } = useAuth();
  const [section, setSection] = useState("overview");
  const nav = useNavigate();

  useEffect(() => {
    if (!checking && (!user || user.role !== "admin")) nav("/admin");
  }, [checking, user, nav]);

  if (checking || !user) return <div className="min-h-screen flex items-center justify-center text-slate-400">Loading…</div>;

  const navItems = [
    { id: "overview", label: "Overview", icon: Gauge },
    { id: "campaigns", label: "Campaigns", icon: Megaphone },
    { id: "markets", label: "Markets", icon: MapPin },
    { id: "categories", label: "Categories", icon: SquaresFour },
    { id: "salesreps", label: "Sales Reps", icon: UsersThree },
    { id: "mailer", label: "Mailer Canvas", icon: SquaresFour },
    { id: "creative", label: "Creative", icon: ImageIcon },
    { id: "roi", label: "Market ROI", icon: ChartBar },
    { id: "pricing", label: "Pricing", icon: CurrencyDollar },
    { id: "subscribers", label: "Subscribers", icon: Users },
    { id: "settings", label: "Settings", icon: GearSix },
  ];

  return (
    <div className="min-h-screen bg-slate-100 flex">
      <aside className="w-60 bg-navy-900 text-slate-300 flex flex-col fixed h-full">
        <div className="p-6 border-b border-navy-800">
          <Link to="/" className="font-display text-xl text-white tracking-tight">PMI Admin</Link>
          <p className="text-xs text-slate-500 mt-1">Master Control</p>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {navItems.map((n) => (
            <button key={n.id} onClick={() => setSection(n.id)} data-testid={`admin-nav-${n.id}`}
              className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-[600] transition-colors ${
                section === n.id ? "bg-amber-600 text-white" : "hover:bg-navy-800 text-slate-400"}`}>
              <n.icon size={18} weight="duotone" /> {n.label}
            </button>
          ))}
        </nav>
        <button onClick={() => { logout(); nav("/admin"); }} data-testid="admin-logout" className="m-3 flex items-center gap-3 px-4 py-3 text-sm text-slate-400 hover:bg-navy-800">
          <SignOut size={18} /> Sign out
        </button>
      </aside>

      <main className="flex-1 ml-60 p-10">
        {section === "overview" && <Overview go={setSection} />}
        {section === "campaigns" && <Campaigns />}
        {section === "markets" && <Markets />}
        {section === "categories" && <Categories />}
        {section === "salesreps" && <SalesReps />}
        {section === "creative" && <CreativeReview />}
        {section === "mailer" && <AdminMailer />}
        {section === "roi" && <RoiDashboard />}
        {section === "pricing" && <Pricing />}
        {section === "subscribers" && <Subscribers user={user} />}
        {section === "settings" && <Settings />}
      </main>
    </div>
  );
}

function SectionHead({ eyebrow, title }) {
  return (
    <div className="mb-8">
      <p className="text-xs uppercase tracking-[0.2em] font-bold text-amber-600">{eyebrow}</p>
      <h1 className="font-display text-3xl sm:text-4xl tracking-tight mt-1">{title}</h1>
    </div>
  );
}

function Overview({ go }) {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {}); }, []);
  if (!stats) return <p className="text-slate-400">Loading stats…</p>;
  const cards = [
    { label: "Revenue", value: `$${stats.revenue.toFixed(0)}`, icon: CurrencyDollar, color: "text-emerald-700" },
    { label: "Campaigns", value: stats.campaigns, icon: Megaphone, color: "text-navy-900" },
    { label: "Slots Sold", value: stats.slots_sold, icon: Ticket, color: "text-amber-600" },
    { label: "Subscribers", value: stats.subscribers, icon: Users, color: "text-navy-900" },
    { label: "Total Views", value: stats.total_views, icon: Eye, color: "text-navy-900" },
    { label: "Total Redemptions", value: stats.total_redemptions, icon: Ticket, color: "text-emerald-700" },
  ];
  const quick = [
    { id: "campaigns", label: "Manage Campaigns", icon: Megaphone },
    { id: "markets", label: "Edit Markets", icon: MapPin },
    { id: "pricing", label: "Adjust Pricing", icon: CurrencyDollar },
    { id: "subscribers", label: "Subscribers", icon: Users },
  ];
  return (
    <div>
      <SectionHead eyebrow="Dashboard" title="Network Overview" />
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200 border border-slate-200">
        {cards.map((c) => (
          <div key={c.label} className="bg-white p-8" data-testid={`stat-${c.label.toLowerCase().replace(/ /g, "-")}`}>
            <c.icon size={22} weight="duotone" className="text-slate-400" />
            <p className="text-xs uppercase tracking-wider font-bold text-slate-500 mt-4">{c.label}</p>
            <p className={`font-display text-4xl tracking-tight mt-1 ${c.color}`}>{c.value}</p>
          </div>
        ))}
      </div>
      <div className="mt-8">
        <p className="text-xs uppercase tracking-[0.2em] font-bold text-amber-600 mb-3">Quick Actions</p>
        <div className="flex flex-wrap gap-3">
          {quick.map((q) => (
            <button key={q.id} onClick={() => go(q.id)} data-testid={`quick-${q.id}`}
              className="flex items-center gap-2 bg-white border border-slate-200 px-5 py-3 text-sm font-[600] hover:border-navy-900 hover:-translate-y-0.5 transition-all">
              <q.icon size={16} weight="duotone" className="text-slate-500" /> {q.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function Campaigns() {
  const [campaigns, setCampaigns] = useState([]);
  const [slots, setSlots] = useState([]);
  const [active, setActive] = useState("");
  const [form, setForm] = useState({ city: "", state: "", month_year: "", base_price: 495 });
  const [newPrice, setNewPrice] = useState("");
  const [edit, setEdit] = useState({ city: "", state: "", month_year: "" });
  const [slotEdit, setSlotEdit] = useState(null);

  const load = () => api.get("/campaigns").then(({ data }) => { setCampaigns(data); if (!active && data.length) selectCampaign(data[0].id); });
  const selectCampaign = (cid) => { setActive(cid); api.get(`/campaigns/${cid}/slots`).then(({ data }) => setSlots(data)); };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  useEffect(() => {
    const c = campaigns.find((x) => x.id === active);
    if (c) setEdit({ city: c.city, state: c.state, month_year: c.month_year });
  }, [active, campaigns]);

  const deleteCampaign = async () => {
    if (!window.confirm("Delete this campaign along with its slots and subscribers?")) return;
    await api.delete(`/campaigns/${active}`);
    toast.success("Campaign deleted");
    const remaining = campaigns.filter((c) => c.id !== active);
    setCampaigns(remaining);
    if (remaining.length) selectCampaign(remaining[0].id);
    else { setActive(""); setSlots([]); }
  };
  const saveEdit = async () => {
    await api.put(`/campaigns/${active}`, edit);
    toast.success("Campaign updated");
    load();
  };
  const [sched, setSched] = useState("");
  const togglePublish = async () => {
    const next = activeCampaign?.status === "published" ? "draft" : "published";
    await api.put(`/campaigns/${active}`, { status: next });
    toast.success(next === "published" ? "Campaign published" : "Campaign set to draft");
    load();
  };
  const copySponsorLink = async () => {
    const url = `${window.location.origin}/sponsor/${active}`;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(url);
      } else {
        const ta = document.createElement("textarea");
        ta.value = url;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        const ok = document.execCommand("copy");
        document.body.removeChild(ta);
        if (!ok) throw new Error("execCommand failed");
      }
      toast.success("Sponsor link copied");
    } catch (e) {
      toast.error("Couldn't copy automatically — copy this link:", { description: url, duration: 10000 });
    }
  };
  const saveSchedule = async () => { await api.put(`/campaigns/${active}`, { publish_at: sched ? new Date(sched).toISOString() : null }); toast.success(sched ? "Go-live scheduled" : "Schedule saved"); setSched(""); load(); };
  const clearSchedule = async () => { await api.put(`/campaigns/${active}`, { publish_at: null }); toast.success("Go-live schedule cleared"); setSched(""); load(); };
  const uploadCardArt = async (file, face) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    await api.put(`/campaigns/${active}`, { [face === "Front" ? "front_image_id" : "back_image_id"]: data.file_id });
    toast.success(`${face} card art updated`);
    load();
  };

  const uploadSlotAsset = async (file, key) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    setSlotEdit((se) => ({ ...se, [key]: data.file_id }));
    toast.success("Image uploaded");
  };
  const saveSlot = async () => {
    try {
      await api.patch(`/slots/${slotEdit.id}`, {
        status: slotEdit.status,
        business_name: slotEdit.business_name || "",
        offer_details: slotEdit.offer_details || "",
        logo_file_id: slotEdit.logo_file_id || "",
        coupon_file_id: slotEdit.coupon_file_id || "",
      });
      toast.success("Slot updated");
      setSlotEdit(null);
      selectCampaign(active);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const createCampaign = async (e) => {
    e.preventDefault();
    try {
      await api.post("/campaigns", form);
      toast.success("Campaign created with 16 slots");
      setForm({ city: "", state: "", month_year: "", base_price: 495 });
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const resetSlots = async () => {
    await api.post(`/campaigns/${active}/reset`);
    toast.success("All slots reset to Available");
    selectCampaign(active);
  };

  const applyPrice = async () => {
    if (!newPrice) return;
    await api.post(`/campaigns/${active}/price?price=${newPrice}`);
    toast.success(`Base price set to $${newPrice}`);
    setNewPrice("");
    selectCampaign(active);
    load();
  };

  const activeCampaign = campaigns.find((c) => c.id === active);
  const reserved = slots.filter((s) => s.status === "Reserved").length;

  return (
    <div>
      <SectionHead eyebrow="Master Control" title="Campaign Manager" />
      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          <div className="flex flex-wrap items-center gap-3">
            <select value={active} onChange={(e) => selectCampaign(e.target.value)} data-testid="admin-campaign-select"
              className="border border-slate-300 px-4 py-2.5 bg-white focus:ring-2 focus:ring-navy-900 focus:outline-none">
              {campaigns.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
            </select>
            <button onClick={resetSlots} data-testid="reset-slots-btn"
              className="flex items-center gap-2 border border-slate-300 px-4 py-2.5 text-sm font-[600] hover:border-navy-900 transition-colors">
              <ArrowClockwise size={16} /> Reset Slots
            </button>
            <button onClick={deleteCampaign} data-testid="delete-campaign-btn"
              className="flex items-center gap-2 border border-red-300 text-red-600 px-4 py-2.5 text-sm font-[600] hover:bg-red-50 transition-colors">
              <Trash size={16} /> Delete
            </button>
            <button onClick={togglePublish} data-testid="publish-toggle-btn"
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-[600] transition-colors ${activeCampaign?.status === "published" ? "border border-slate-300 hover:border-navy-900" : "bg-emerald-700 text-white hover:bg-emerald-800"}`}>
              {activeCampaign?.status === "published" ? "Unpublish" : "Publish"}
            </button>
            <span data-testid="campaign-status-badge" className={`px-3 py-1 text-xs font-bold uppercase tracking-wider ${activeCampaign?.status === "published" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
              {activeCampaign?.status || "draft"}
            </span>
            <button onClick={copySponsorLink} data-testid="copy-sponsor-link-btn"
              className="flex items-center gap-2 border border-slate-300 px-4 py-2.5 text-sm font-[600] hover:border-navy-900 transition-colors">
              Copy Sponsor Link
            </button>
          </div>

          {activeCampaign && (
            <div className="bg-white border border-slate-200 p-6">
              <div className="flex flex-col gap-3 mb-4">
                <div>
                  <h3 className="font-display text-2xl tracking-tight">{activeCampaign.label}</h3>
                  <p className="text-sm text-slate-500 mt-1">{reserved} / 16 slots reserved · base ${activeCampaign.base_price}</p>
                </div>
                <div className="flex flex-wrap items-end gap-2 border-t border-slate-100 pt-4">
                  <div>
                    <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500 block mb-1">City</label>
                    <input value={edit.city} onChange={(e) => setEdit({ ...edit, city: e.target.value })} data-testid="edit-city" className="border border-slate-300 px-3 py-2 text-sm w-32 focus:ring-2 focus:ring-navy-900 focus:outline-none" />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500 block mb-1">State</label>
                    <input value={edit.state} onChange={(e) => setEdit({ ...edit, state: e.target.value })} data-testid="edit-state" className="border border-slate-300 px-3 py-2 text-sm w-20 focus:ring-2 focus:ring-navy-900 focus:outline-none" />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500 block mb-1">Month/Year</label>
                    <input value={edit.month_year} onChange={(e) => setEdit({ ...edit, month_year: e.target.value })} data-testid="edit-month" className="border border-slate-300 px-3 py-2 text-sm w-32 focus:ring-2 focus:ring-navy-900 focus:outline-none" />
                  </div>
                  <button onClick={saveEdit} data-testid="save-campaign-btn" className="flex items-center gap-1 bg-navy-900 text-white px-4 py-2 text-sm font-[600] hover:bg-emerald-700 transition-colors">
                    <PencilSimple size={14} /> Save
                  </button>
                </div>
                <div className="flex flex-wrap items-end gap-4 border-t border-slate-100 pt-4">
                  {["Front", "Back"].map((f) => {
                    const id = f === "Front" ? activeCampaign.front_image_id : activeCampaign.back_image_id;
                    return (
                      <div key={f}>
                        <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500 block mb-1">{f} card art</label>
                        {id ? <img src={fileUrl(id)} alt={`${f} card`} className="h-16 w-auto border border-slate-200 mb-1" /> : <div className="h-16 w-24 bg-slate-100 border border-slate-200 mb-1 flex items-center justify-center text-[10px] text-slate-400">None</div>}
                        <label data-testid={`upload-card-${f.toLowerCase()}`} className="block text-xs font-[600] text-navy-900 border border-slate-300 px-3 py-1.5 cursor-pointer hover:border-navy-900 text-center">
                          Upload<input type="file" accept="image/*" className="hidden" onChange={(e) => uploadCardArt(e.target.files[0], f)} />
                        </label>
                      </div>
                    );
                  })}
                </div>
                <div className="flex flex-wrap items-end gap-2 border-t border-slate-100 pt-4">
                  <div>
                    <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500 block mb-1">Auto-publish at</label>
                    <input type="datetime-local" value={sched} onChange={(e) => setSched(e.target.value)} data-testid="schedule-input" className="border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-navy-900 focus:outline-none" />
                  </div>
                  <button onClick={saveSchedule} data-testid="schedule-save-btn" className="bg-navy-900 text-white px-4 py-2 text-sm font-[600] hover:bg-emerald-700 transition-colors">Schedule Go-Live</button>
                  {activeCampaign.publish_at && <button onClick={clearSchedule} data-testid="schedule-clear-btn" className="border border-slate-300 text-slate-600 px-4 py-2 text-sm font-[600] hover:border-red-500 hover:text-red-600 transition-colors">Clear</button>}
                  {activeCampaign.publish_at && <span className="text-xs text-slate-500 self-center" data-testid="schedule-current">Live at {new Date(activeCampaign.publish_at).toLocaleString()}</span>}
                </div>
              </div>
              <div className="flex items-center gap-2 mb-6">
                <input type="number" placeholder="New base price" value={newPrice} onChange={(e) => setNewPrice(e.target.value)} data-testid="base-price-input"
                  className="border border-slate-300 px-3 py-2 text-sm w-40 focus:ring-2 focus:ring-navy-900 focus:outline-none" />
                <button onClick={applyPrice} data-testid="apply-price-btn" className="bg-navy-900 text-white px-4 py-2 text-sm font-[600] hover:bg-emerald-700 transition-colors">Apply to available</button>
              </div>
              <div className="grid grid-cols-4 gap-px bg-slate-200 border border-slate-200">
                {slots.map((s) => (
                  <button key={s.id} onClick={() => setSlotEdit({ ...s })} className={`p-3 text-center transition-colors hover:bg-amber-50 ${s.status === "Reserved" ? "bg-slate-100" : "bg-white"}`} data-testid={`admin-slot-${s.face}-${s.slot_number}`}>
                    <p className="text-[10px] uppercase tracking-wider font-bold text-slate-400">{s.face[0]}{s.slot_number}</p>
                    <p className={`text-xs font-[600] mt-1 ${s.status === "Reserved" ? "text-slate-500" : "text-emerald-700"}`}>{s.status === "Reserved" ? "Sold" : "Open"}</p>
                    <p className="text-[11px] text-slate-400 truncate">{s.business_name || `$${s.price}`}</p>
                  </button>
                ))}
              </div>
              {slotEdit && (
                <div className="mt-4 bg-white border-2 border-navy-900 p-6" data-testid="admin-slot-edit-panel">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="font-display text-xl tracking-tight">Edit Slot · {slotEdit.face} {slotEdit.slot_number}</h4>
                    <button onClick={() => setSlotEdit(null)} data-testid="slot-edit-cancel" className="text-sm text-slate-400 hover:text-navy-900">Cancel</button>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500 block mb-1">Status</label>
                      <select value={slotEdit.status} onChange={(e) => setSlotEdit({ ...slotEdit, status: e.target.value })} data-testid="slot-edit-status" className="w-full border border-slate-300 px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-navy-900 focus:outline-none">
                        <option value="Available">Open (Available)</option>
                        <option value="Reserved">Sold (Reserved)</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500 block mb-1">Business name</label>
                      <input value={slotEdit.business_name || ""} onChange={(e) => setSlotEdit({ ...slotEdit, business_name: e.target.value })} data-testid="slot-edit-business" className="w-full border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-navy-900 focus:outline-none" />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500 block mb-1">Custom offer text</label>
                      <textarea value={slotEdit.offer_details || ""} onChange={(e) => setSlotEdit({ ...slotEdit, offer_details: e.target.value })} data-testid="slot-edit-offer" rows={2} className="w-full border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-navy-900 focus:outline-none" />
                    </div>
                    {[["logo_file_id", "Logo", "slot-edit-logo"], ["coupon_file_id", "Coupon image", "slot-edit-coupon"]].map(([key, label, tid]) => (
                      <div key={key}>
                        <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500 block mb-1">{label}</label>
                        {slotEdit[key] ? <img src={fileUrl(slotEdit[key])} alt={label} className="h-14 w-auto border border-slate-200 mb-1" /> : <div className="h-14 w-20 bg-slate-100 border border-slate-200 mb-1 flex items-center justify-center text-[10px] text-slate-400">None</div>}
                        <label data-testid={tid} className="block text-xs font-[600] text-navy-900 border border-slate-300 px-3 py-1.5 cursor-pointer hover:border-navy-900 text-center w-fit">
                          Upload<input type="file" accept="image/*" className="hidden" onChange={(e) => uploadSlotAsset(e.target.files[0], key)} />
                        </label>
                      </div>
                    ))}
                  </div>
                  <button onClick={saveSlot} data-testid="slot-edit-save" className="mt-5 bg-navy-900 text-white px-6 py-2.5 text-sm font-[600] hover:bg-emerald-700 transition-colors">Save Slot</button>
                </div>
              )}
            </div>
          )}
        </div>

        <div>
          <form onSubmit={createCampaign} className="bg-white border border-slate-200 p-6 space-y-3" data-testid="new-campaign-form">
            <h3 className="font-display text-xl tracking-tight flex items-center gap-2"><Plus size={18} /> New Campaign</h3>
            <input required placeholder="City (e.g. Miami)" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="nc-city" className="w-full border border-slate-300 px-3 py-2.5 text-sm focus:ring-2 focus:ring-navy-900 focus:outline-none" />
            <input required placeholder="State (e.g. FL)" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} data-testid="nc-state" className="w-full border border-slate-300 px-3 py-2.5 text-sm focus:ring-2 focus:ring-navy-900 focus:outline-none" />
            <input required placeholder="Month/Year (e.g. Oct 2026)" value={form.month_year} onChange={(e) => setForm({ ...form, month_year: e.target.value })} data-testid="nc-month" className="w-full border border-slate-300 px-3 py-2.5 text-sm focus:ring-2 focus:ring-navy-900 focus:outline-none" />
            <input required type="number" placeholder="Base price" value={form.base_price} onChange={(e) => setForm({ ...form, base_price: Number(e.target.value) })} data-testid="nc-price" className="w-full border border-slate-300 px-3 py-2.5 text-sm focus:ring-2 focus:ring-navy-900 focus:outline-none" />
            <button type="submit" data-testid="create-campaign-btn" className="w-full bg-navy-900 text-white py-3 font-[600] hover:bg-emerald-700 transition-colors">Create Campaign</button>
          </form>
        </div>
      </div>
    </div>
  );
}

function Markets() {
  const [locs, setLocs] = useState([]);
  const [form, setForm] = useState({ name: "", state: "" });
  const [editId, setEditId] = useState("");
  const [editForm, setEditForm] = useState({ name: "", state: "" });
  const load = () => api.get("/locations?include_archived=true").then(({ data }) => setLocs(data));
  useEffect(() => { load(); }, []);
  const add = async (e) => { e.preventDefault(); if (!form.name) return; await api.post("/locations", form); toast.success("Market added"); setForm({ name: "", state: "" }); load(); };
  const archive = async (id) => { await api.delete(`/locations/${id}`); toast.success("Market archived"); load(); };
  const reactivate = async (id) => { await api.put(`/locations/${id}`, { status: "active" }); toast.success("Market reactivated"); load(); };
  const remove = async (id) => { if (!window.confirm("Permanently delete this market? This cannot be undone.")) return; await api.delete(`/locations/${id}?hard=true`); toast.success("Market deleted"); load(); };
  const startEdit = (l) => { setEditId(l.id); setEditForm({ name: l.name, state: l.state || "" }); };
  const saveEdit = async (id) => { await api.put(`/locations/${id}`, { name: editForm.name, state: editForm.state }); toast.success("Market updated"); setEditId(""); load(); };
  return (
    <div>
      <SectionHead eyebrow="Global Regions" title="Market Locations" />
      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 bg-white border border-slate-200" data-testid="markets-table">
          <table className="w-full text-sm">
            <thead className="bg-slate-100 text-left text-xs uppercase tracking-wider text-slate-500">
              <tr><th className="px-6 py-3">Market</th><th className="px-6 py-3">State</th><th className="px-6 py-3">Status</th><th className="px-6 py-3 text-right">Actions</th></tr>
            </thead>
            <tbody>
              {locs.map((l) => (
                <tr key={l.id} className="border-t border-slate-100" data-testid={`market-row-${l.id}`}>
                  {editId === l.id ? (
                    <>
                      <td className="px-6 py-3"><input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} data-testid={`market-edit-name-${l.id}`} className="border border-slate-300 px-2 py-1.5 text-sm w-full focus:ring-2 focus:ring-navy-900 focus:outline-none" /></td>
                      <td className="px-6 py-3"><input value={editForm.state} onChange={(e) => setEditForm({ ...editForm, state: e.target.value })} data-testid={`market-edit-state-${l.id}`} className="border border-slate-300 px-2 py-1.5 text-sm w-16 focus:ring-2 focus:ring-navy-900 focus:outline-none" /></td>
                      <td className="px-6 py-3 text-slate-400 text-xs">{l.status}</td>
                      <td className="px-6 py-3 text-right whitespace-nowrap">
                        <button onClick={() => saveEdit(l.id)} data-testid={`market-save-${l.id}`} className="text-xs font-[600] text-emerald-700 hover:underline mr-3">Save</button>
                        <button onClick={() => setEditId("")} className="text-xs font-[600] text-slate-400 hover:underline">Cancel</button>
                      </td>
                    </>
                  ) : (
                    <>
                      <td className="px-6 py-4 font-[600]">{l.name}</td>
                      <td className="px-6 py-4 text-slate-500">{l.state}</td>
                      <td className="px-6 py-4"><span className={`text-xs font-bold uppercase ${l.status === "active" ? "text-emerald-700" : "text-slate-400"}`}>{l.status}</span></td>
                      <td className="px-6 py-4 text-right whitespace-nowrap">
                        <button onClick={() => startEdit(l)} data-testid={`market-edit-${l.id}`} className="text-xs font-[600] text-navy-900 hover:underline mr-3">Edit</button>
                        {l.status === "active"
                          ? <button onClick={() => archive(l.id)} data-testid={`market-archive-${l.id}`} className="text-xs font-[600] text-amber-600 hover:underline mr-3">Archive</button>
                          : <button onClick={() => reactivate(l.id)} className="text-xs font-[600] text-emerald-700 hover:underline mr-3">Reactivate</button>}
                        <button onClick={() => remove(l.id)} data-testid={`market-delete-${l.id}`} className="text-xs font-[600] text-red-600 hover:underline">Delete</button>
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <form onSubmit={add} className="bg-white border border-slate-200 p-6 space-y-3 h-fit" data-testid="new-market-form">
          <h3 className="font-display text-xl tracking-tight flex items-center gap-2"><Plus size={18} /> New Market</h3>
          <input required placeholder="Region name (e.g. Charlotte)" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="market-name" className="w-full border border-slate-300 px-3 py-2.5 text-sm focus:ring-2 focus:ring-navy-900 focus:outline-none" />
          <input placeholder="State (e.g. NC)" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} data-testid="market-state" className="w-full border border-slate-300 px-3 py-2.5 text-sm focus:ring-2 focus:ring-navy-900 focus:outline-none" />
          <button type="submit" data-testid="add-market-btn" className="w-full bg-navy-900 text-white py-3 font-[600] hover:bg-emerald-700 transition-colors">Add Market</button>
        </form>
      </div>
    </div>
  );
}

function Subscribers({ user }) {
  const [subs, setSubs] = useState([]);
  const [zip, setZip] = useState("");
  const [city, setCity] = useState("");

  const load = () => {
    const params = {};
    if (zip) params.zip_code = zip;
    if (city) params.city = city;
    api.get("/admin/subscribers", { params }).then(({ data }) => setSubs(data));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const remove = async (id) => {
    if (!window.confirm("Permanently delete this subscriber?")) return;
    try { await api.delete(`/admin/subscribers/${id}`); toast.success("Subscriber deleted"); setSubs((prev) => prev.filter((s) => s.id !== id)); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const exportCsv = () => {
    const qs = new URLSearchParams();
    if (zip) qs.set("zip_code", zip);
    if (city) qs.set("city", city);
    fetch(`${API}/admin/subscribers/export.csv?${qs.toString()}`, { credentials: "include" })
      .then((r) => { if (!r.ok) throw new Error(); return r.blob(); })
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = "subscribers.csv"; a.click();
        URL.revokeObjectURL(url);
      })
      .catch(() => toast.error("Export failed"));
  };

  return (
    <div>
      <SectionHead eyebrow="Consumer Data" title="Subscriber Lists" />
      <div className="flex flex-wrap items-end gap-3 mb-6">
        <div>
          <label className="text-xs uppercase tracking-wider font-bold text-slate-500 block mb-1">Zip code</label>
          <input value={zip} onChange={(e) => setZip(e.target.value)} data-testid="filter-zip" placeholder="e.g. 32801" className="border border-slate-300 px-3 py-2.5 text-sm focus:ring-2 focus:ring-navy-900 focus:outline-none" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider font-bold text-slate-500 block mb-1">City</label>
          <input value={city} onChange={(e) => setCity(e.target.value)} data-testid="filter-city" placeholder="e.g. Orlando" className="border border-slate-300 px-3 py-2.5 text-sm focus:ring-2 focus:ring-navy-900 focus:outline-none" />
        </div>
        <button onClick={load} data-testid="apply-filter-btn" className="bg-navy-900 text-white px-5 py-2.5 text-sm font-[600] hover:bg-emerald-700 transition-colors">Filter</button>
        <button onClick={exportCsv} data-testid="export-csv-btn" className="flex items-center gap-2 border border-slate-300 px-5 py-2.5 text-sm font-[600] hover:border-navy-900 transition-colors">
          <DownloadSimple size={16} /> Export CSV
        </button>
      </div>

      <div className="bg-white border border-slate-200">
        <table className="w-full text-sm">
          <thead className="bg-slate-100 text-left text-xs uppercase tracking-wider text-slate-500">
            <tr><th className="px-6 py-3">Name</th><th className="px-6 py-3">Email</th><th className="px-6 py-3">Zip</th><th className="px-6 py-3">Signed Up</th><th className="px-6 py-3"></th></tr>
          </thead>
          <tbody data-testid="subscribers-table">
            {subs.length === 0 ? (
              <tr><td colSpan={5} className="px-6 py-10 text-center text-slate-400">No subscribers match.</td></tr>
            ) : subs.map((s) => (
              <tr key={s.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`subscriber-row-${s.id}`}>
                <td className="px-6 py-4 font-[600]">{s.name}</td>
                <td className="px-6 py-4 text-slate-600">{s.email}</td>
                <td className="px-6 py-4">{s.zip_code}</td>
                <td className="px-6 py-4 text-slate-400">{new Date(s.created_at).toLocaleDateString()}</td>
                <td className="px-6 py-4 text-right">
                  <button onClick={() => remove(s.id)} data-testid={`subscriber-delete-${s.id}`} className="inline-flex items-center gap-1 text-xs font-[600] text-red-600 hover:underline">
                    <Trash size={14} /> Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Settings() {
  const [form, setForm] = useState(null);
  useEffect(() => { api.get("/settings").then(({ data }) => setForm(data)); }, []);
  const save = async (e) => {
    e.preventDefault();
    try {
      await api.put("/settings", form);
      toast.success("Global settings updated");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  if (!form) return <p className="text-slate-400">Loading…</p>;
  const fields = [
    ["company_name", "Company Name"], ["tagline", "Tagline"], ["phone", "Phone"],
    ["email", "Support Email"], ["address", "Address"], ["support_hours", "Support Hours"],
  ];
  return (
    <div className="max-w-2xl">
      <SectionHead eyebrow="Global" title="Company Settings" />
      <form onSubmit={save} className="bg-white border border-slate-200 p-8 space-y-4" data-testid="settings-form">
        {fields.map(([key, label]) => (
          <div key={key}>
            <label className="text-xs uppercase tracking-wider font-bold text-slate-500 block mb-1">{label}</label>
            <input value={form[key] || ""} onChange={(e) => setForm({ ...form, [key]: e.target.value })} data-testid={`settings-${key}`}
              className="w-full border border-slate-300 px-4 py-3 focus:ring-2 focus:ring-navy-900 focus:outline-none" />
          </div>
        ))}
        <button type="submit" data-testid="save-settings-btn" className="bg-navy-900 text-white px-8 py-3 font-[600] hover:bg-emerald-700 transition-colors">Save Settings</button>
      </form>
    </div>
  );
}

function RoiDashboard() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/admin/roi").then(({ data }) => setRows(data)).catch(() => {}); }, []);
  const cols = [["campaigns", "Campaigns"], ["subscribers", "Subscribers"], ["leads", "Leads"], ["slots_sold", "Slots Sold"], ["views", "Views"], ["redemptions", "Redemptions"]];
  return (
    <div>
      <SectionHead eyebrow="Per-Market Performance" title="Location ROI" />
      <div className="bg-white border border-slate-200 overflow-x-auto" data-testid="roi-table">
        <table className="w-full text-sm">
          <thead className="bg-slate-100 text-left text-xs uppercase tracking-wider text-slate-500">
            <tr>
              <th className="px-6 py-3">Market</th>
              {cols.map(([k, l]) => <th key={k} className="px-6 py-3 text-right">{l}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={7} className="px-6 py-10 text-center text-slate-400">No market data yet.</td></tr>
            ) : rows.map((r) => (
              <tr key={r.location_id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`roi-row-${r.location_id}`}>
                <td className="px-6 py-4 font-[600]">{r.name}<span className="text-slate-400 font-normal"> · {r.state}</span>{r.status === "archived" && <span className="ml-2 text-[10px] uppercase tracking-wider font-bold text-slate-400 bg-slate-100 px-1.5 py-0.5">archived</span>}</td>
                {cols.map(([k]) => <td key={k} className={`px-6 py-4 text-right tabular-nums ${k === "redemptions" ? "text-emerald-700 font-[600]" : ""}`}>{r[k]}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Pricing() {
  const [p, setP] = useState(null);
  useEffect(() => { api.get("/pricing").then(({ data }) => setP(data)); }, []);
  const updatePkg = (i, key, val) => setP((prev) => ({ ...prev, ai_packages: prev.ai_packages.map((a, idx) => idx === i ? { ...a, [key]: val } : a) }));
  const addPkg = () => setP((prev) => ({ ...prev, ai_packages: [...(prev.ai_packages || []), { id: `svc_${Date.now()}`, name: "New AI Service", desc: "", setup_price: 0, monthly_price: 0 }] }));
  const removePkg = (i) => { if (!window.confirm("Remove this AI service? Save to persist.")) return; setP((prev) => ({ ...prev, ai_packages: prev.ai_packages.filter((_, idx) => idx !== i) })); };
  const save = async () => {
    try {
      await api.put("/pricing", {
        base_slot_price: Number(p.base_slot_price),
        regional_discount_pct: Number(p.regional_discount_pct),
        half_slot_price: Number(p.half_slot_price),
        full_slot_price: Number(p.full_slot_price),
        ai_packages: (p.ai_packages || []).map((a) => ({ ...a, setup_price: Number(a.setup_price), monthly_price: Number(a.monthly_price) })),
      });
      toast.success("Pricing updated");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  if (!p) return <p className="text-slate-400">Loading…</p>;
  return (
    <div className="max-w-3xl">
      <SectionHead eyebrow="Master Control" title="Pricing & Overrides" />
      <div className="bg-white border border-slate-200 p-8 space-y-6" data-testid="pricing-form">
        <div className="grid sm:grid-cols-2 gap-6">
          <div>
            <label className="text-xs uppercase tracking-wider font-bold text-slate-500 block mb-1">Base Slot Price ($)</label>
            <input type="number" min="0" value={p.base_slot_price} onChange={(e) => setP({ ...p, base_slot_price: e.target.value })} data-testid="pricing-base-price" className="w-full border border-slate-300 px-4 py-3 focus:ring-2 focus:ring-navy-900 focus:outline-none" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider font-bold text-slate-500 block mb-1">Regional Discount (%)</label>
            <input type="number" min="0" max="50" value={p.regional_discount_pct} onChange={(e) => setP({ ...p, regional_discount_pct: e.target.value })} data-testid="pricing-discount" className="w-full border border-slate-300 px-4 py-3 focus:ring-2 focus:ring-navy-900 focus:outline-none" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider font-bold text-slate-500 block mb-1">Half-Slot Price ($)</label>
            <input type="number" min="0" value={p.half_slot_price ?? 297} onChange={(e) => setP({ ...p, half_slot_price: e.target.value })} data-testid="pricing-half-price" className="w-full border border-slate-300 px-4 py-3 focus:ring-2 focus:ring-navy-900 focus:outline-none" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider font-bold text-slate-500 block mb-1">Full-Slot Price ($)</label>
            <input type="number" min="0" value={p.full_slot_price ?? 500} onChange={(e) => setP({ ...p, full_slot_price: e.target.value })} data-testid="pricing-full-price" className="w-full border border-slate-300 px-4 py-3 focus:ring-2 focus:ring-navy-900 focus:outline-none" />
          </div>
        </div>
        <div className="border-t border-slate-100 pt-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-xl tracking-tight">AI Agent Packages</h3>
            <button onClick={addPkg} data-testid="add-ai-service-btn" className="flex items-center gap-1 border border-slate-300 px-3 py-1.5 text-xs font-[600] hover:border-navy-900 transition-colors"><Plus size={14} /> Add New AI Service</button>
          </div>
          <div className="space-y-4">
            {(p.ai_packages || []).map((a, i) => (
              <div key={a.id} className="border border-slate-200 p-4 space-y-3" data-testid={`pricing-ai-${a.id}`}>
                <div className="flex items-center gap-3">
                  <div className="flex-1">
                    <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500 block mb-1">Service name</label>
                    <input value={a.name} onChange={(e) => updatePkg(i, "name", e.target.value)} data-testid={`pricing-ai-name-${a.id}`} className="w-full border border-slate-300 px-3 py-2 text-sm font-[600] focus:ring-2 focus:ring-navy-900 focus:outline-none" />
                  </div>
                  <button onClick={() => removePkg(i)} data-testid={`pricing-ai-delete-${a.id}`} className="mt-5 text-red-600 hover:bg-red-50 p-2 transition-colors" title="Remove service"><Trash size={16} /></button>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500 block mb-1">Description (shown to merchants)</label>
                  <textarea value={a.desc || ""} onChange={(e) => updatePkg(i, "desc", e.target.value)} data-testid={`pricing-ai-desc-${a.id}`} rows={2} placeholder="What this service does…" className="w-full border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-navy-900 focus:outline-none" />
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500 block mb-1">Setup ($)</label>
                    <input type="number" min="0" value={a.setup_price} onChange={(e) => updatePkg(i, "setup_price", e.target.value)} data-testid={`pricing-ai-setup-${a.id}`} className="w-full border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-navy-900 focus:outline-none" />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500 block mb-1">Monthly ($)</label>
                    <input type="number" min="0" value={a.monthly_price} onChange={(e) => updatePkg(i, "monthly_price", e.target.value)} data-testid={`pricing-ai-monthly-${a.id}`} className="w-full border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-navy-900 focus:outline-none" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <button onClick={save} data-testid="save-pricing-btn" className="bg-navy-900 text-white px-8 py-3 font-[600] hover:bg-emerald-700 transition-colors">Save Pricing</button>
      </div>
    </div>
  );
}


function Categories() {
  const [cats, setCats] = useState([]);
  const [zones, setZones] = useState([]);
  const [pricing, setPricing] = useState({ half_slot_price: 297, full_slot_price: 500 });
  const [newCat, setNewCat] = useState("");
  const [editId, setEditId] = useState("");
  const [editName, setEditName] = useState("");
  const [zip, setZip] = useState("");
  const [busy, setBusy] = useState(false);
  const [dragId, setDragId] = useState(null);
  const [host, setHost] = useState(null);
  const [hostUploading, setHostUploading] = useState(false);

  const loadCats = () => api.get("/admin/categories").then(({ data }) => setCats(data)).catch(() => {});
  const loadZones = () => api.get("/admin/zones").then(({ data }) => setZones(data)).catch(() => {});
  const loadHost = () => api.get("/admin/host-reserve").then(({ data }) => setHost(data)).catch(() => {});
  useEffect(() => { loadCats(); loadZones(); loadHost(); api.get("/pricing").then(({ data }) => setPricing(data)).catch(() => {}); }, []);

  const saveHost = async () => {
    try {
      const { data } = await api.put("/admin/host-reserve", { enabled: host.enabled, brand: host.brand, categoryIds: host.categoryIds, placement: host.placement, logoFileId: host.logoFileId });
      toast.success(data.warnings?.length ? `Applied with ${data.warnings.length} conflict warning(s)` : "Host reserve applied to every zone");
      (data.warnings || []).forEach((w) => toast.warning(w));
      loadCats(); loadZones(); loadHost();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const setHostCat = (idx, val) => setHost((h) => { const ids = [...(h.categoryIds || ["", ""])]; ids[idx] = val; return { ...h, categoryIds: ids }; });

  const add = async (e) => {
    e.preventDefault();
    const name = newCat.trim();
    if (!name) return;
    try { await api.post("/admin/categories", { name }); toast.success(`Added "${name}" — backfilled to all zones`); setNewCat(""); loadCats(); loadZones(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const startEdit = (c) => { setEditId(c.id); setEditName(c.name); };
  const saveEdit = async (id) => {
    const name = editName.trim();
    if (!name) return;
    try { await api.patch(`/admin/categories/${id}`, { name }); toast.success("Category renamed — updated on existing slots"); setEditId(""); loadCats(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const remove = async (c) => {
    if (!window.confirm(`Delete "${c.name}"? Available slots are freed; held/sold slots are kept.`)) return;
    try { const { data } = await api.delete(`/admin/categories/${c.id}`); toast.success(data.retainedActiveSlots ? `Deleted — ${data.retainedActiveSlots} active slot(s) retained` : "Category deleted"); loadCats(); loadZones(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const savePrice = async (c, field, raw) => {
    const val = raw === "" ? null : Number(raw);
    if (val === (c[field] ?? null)) return;
    try { await api.patch(`/admin/categories/${c.id}/pricing`, { [field]: val }); loadCats(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const onDrop = async (targetId) => {
    if (!dragId || dragId === targetId) { setDragId(null); return; }
    const ids = cats.map((c) => c.id);
    const from = ids.indexOf(dragId), to = ids.indexOf(targetId);
    ids.splice(to, 0, ids.splice(from, 1)[0]);
    setCats(ids.map((id) => cats.find((c) => c.id === id)));
    setDragId(null);
    try { await api.patch("/admin/categories-reorder", { orderedIds: ids }); toast.success("Category order saved"); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); loadCats(); }
  };
  const generateZone = async (e) => {
    e.preventDefault();
    const z = zip.trim();
    if (!z) return;
    setBusy(true);
    try { const { data } = await api.post("/admin/zones", { zipCode: z }); toast.success(data.created ? `Generated ${data.created} half-slot(s) for ${z}` : `${z} already fully generated`); setZip(""); loadZones(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const removeZone = async (z) => {
    if (!window.confirm(`Delete zone ${z.zipCode}? This removes all ${z.total} slots${z.sold ? ` including ${z.sold} SOLD` : ""}. This cannot be undone.`)) return;
    try { const { data } = await api.delete(`/admin/zones/${z.zipCode}`); toast.success(`Zone ${z.zipCode} deleted (${data.removedSlots} slots)`); loadZones(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const hg = pricing.half_slot_price ?? 297, fg = pricing.full_slot_price ?? 500;

  return (
    <div>
      <SectionHead eyebrow="Postcard Inventory" title="Business Categories" />
      <p className="text-sm text-slate-500 -mt-6 mb-8">Each zone auto-generates 2 half-slots per category{cats.length ? <> (<span className="font-[600] text-navy-900">{cats.length * 2}</span> half-slots across <span className="font-[600] text-navy-900">{cats.length}</span> categories)</> : ""}. Drag rows to reorder the postcard grid; blank price fields use the global default.</p>
      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 bg-white border border-slate-200 overflow-x-auto" data-testid="categories-table">
          <table className="w-full text-sm">
            <thead className="bg-slate-100 text-left text-xs uppercase tracking-wider text-slate-500">
              <tr><th className="px-4 py-3">#</th><th className="px-4 py-3">Category</th><th className="px-4 py-3 text-right">Half $</th><th className="px-4 py-3 text-right">Full $</th><th className="px-4 py-3 text-right">Actions</th></tr>
            </thead>
            <tbody>
              {cats.map((c, i) => (
                <tr key={c.id} className={`border-t border-slate-100 ${dragId === c.id ? "opacity-40" : ""}`} data-testid={`category-row-${c.id}`}
                  draggable={editId !== c.id} onDragStart={() => setDragId(c.id)} onDragOver={(e) => e.preventDefault()} onDrop={() => onDrop(c.id)}>
                  <td className="px-4 py-4 text-slate-400 tabular-nums"><span className="inline-flex items-center gap-1 cursor-grab" data-testid={`category-drag-${c.id}`}><DotsSixVertical size={14} /> {i + 1}</span></td>
                  {editId === c.id ? (
                    <>
                      <td className="px-4 py-3" colSpan={3}><input autoFocus value={editName} onChange={(e) => setEditName(e.target.value)} data-testid={`category-edit-name-${c.id}`} className="border border-slate-300 px-3 py-1.5 text-sm w-full focus:ring-2 focus:ring-navy-900 focus:outline-none" /></td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <button onClick={() => saveEdit(c.id)} data-testid={`category-save-${c.id}`} className="text-xs font-[600] text-emerald-700 hover:underline mr-3">Save</button>
                        <button onClick={() => setEditId("")} className="text-xs font-[600] text-slate-400 hover:underline">Cancel</button>
                      </td>
                    </>
                  ) : (
                    <>
                      <td className="px-4 py-4 font-[600] text-navy-900">{c.name}</td>
                      <td className="px-4 py-3 text-right">
                        <input key={`h-${c.id}-${c.halfPrice ?? ""}`} type="number" min="0" defaultValue={c.halfPrice ?? ""} placeholder={hg}
                          onBlur={(e) => savePrice(c, "halfPrice", e.target.value)} data-testid={`category-half-${c.id}`}
                          className="w-20 border border-slate-300 px-2 py-1.5 text-sm text-right focus:ring-2 focus:ring-navy-900 focus:outline-none" />
                      </td>
                      <td className="px-4 py-3 text-right">
                        <input key={`f-${c.id}-${c.fullPrice ?? ""}`} type="number" min="0" defaultValue={c.fullPrice ?? ""} placeholder={fg}
                          onBlur={(e) => savePrice(c, "fullPrice", e.target.value)} data-testid={`category-full-${c.id}`}
                          className="w-20 border border-slate-300 px-2 py-1.5 text-sm text-right focus:ring-2 focus:ring-navy-900 focus:outline-none" />
                      </td>
                      <td className="px-4 py-4 text-right whitespace-nowrap">
                        <button onClick={() => startEdit(c)} data-testid={`category-edit-${c.id}`} className="text-xs font-[600] text-navy-900 hover:underline mr-3 inline-flex items-center gap-1"><PencilSimple size={13} /> Rename</button>
                        <button onClick={() => remove(c)} data-testid={`category-delete-${c.id}`} className="text-xs font-[600] text-red-600 hover:underline inline-flex items-center gap-1"><Trash size={13} /> Delete</button>
                      </td>
                    </>
                  )}
                </tr>
              ))}
              {cats.length === 0 && <tr><td colSpan={5} className="px-6 py-10 text-center text-slate-400">No categories yet.</td></tr>}
            </tbody>
          </table>
        </div>

        <div className="space-y-6">
          <form onSubmit={add} className="bg-white border border-slate-200 p-6 space-y-3 h-fit" data-testid="new-category-form">
            <h3 className="font-display text-xl tracking-tight flex items-center gap-2"><Plus size={18} /> New Category</h3>
            <input value={newCat} onChange={(e) => setNewCat(e.target.value)} placeholder="e.g. Window Cleaning" data-testid="category-name-input" className="w-full border border-slate-300 px-3 py-2.5 text-sm focus:ring-2 focus:ring-navy-900 focus:outline-none" />
            <button type="submit" data-testid="add-category-btn" className="w-full bg-navy-900 text-white py-3 font-[600] hover:bg-emerald-700 transition-colors">Add Category</button>
            <p className="text-[11px] text-slate-400">New categories are added as available half-slots across every existing zone.</p>
          </form>

          <form onSubmit={generateZone} className="bg-white border border-slate-200 p-6 space-y-3 h-fit" data-testid="new-zone-form">
            <h3 className="font-display text-xl tracking-tight flex items-center gap-2"><MapPin size={18} /> Generate Zone</h3>
            <input value={zip} onChange={(e) => setZip(e.target.value)} placeholder="Zip code (e.g. 27107)" data-testid="zone-zip-input" className="w-full border border-slate-300 px-3 py-2.5 text-sm focus:ring-2 focus:ring-navy-900 focus:outline-none" />
            <button type="submit" disabled={busy} data-testid="generate-zone-btn" className="w-full bg-amber-600 text-white py-3 font-[600] hover:bg-amber-700 transition-colors disabled:opacity-60">{busy ? "Generating…" : `Generate ${cats.length * 2} Half-Slots`}</button>
          </form>

          {host && (
            <div className="bg-white border border-indigo-200 p-6 space-y-3" data-testid="host-reserve-panel">
              <h3 className="font-display text-xl tracking-tight flex items-center gap-2 text-indigo-700"><GearSix size={18} /> Host Reserve</h3>
              <input value={host.brand} onChange={(e) => setHost({ ...host, brand: e.target.value })} data-testid="host-brand" className="w-full border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-600 focus:outline-none" />
              <div className="flex items-center gap-3">
                {host.logoFileId
                  ? <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${host.logoFileId}`} alt="Host logo" className="h-12 w-12 object-contain border border-slate-200 bg-white shrink-0" data-testid="host-logo-preview" />
                  : <div className="h-12 w-12 border border-dashed border-slate-300 flex items-center justify-center text-[9px] text-slate-400 shrink-0">No logo</div>}
                <label className="flex-1 text-xs font-[600] text-indigo-700 border border-indigo-300 px-3 py-2.5 text-center cursor-pointer hover:bg-indigo-50 transition-colors" data-testid="host-logo-upload">
                  {hostUploading ? "Uploading…" : host.logoFileId ? "Replace logo" : "Upload Eco Envy logo"}
                  <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                    const f = e.target.files?.[0]; if (!f) return;
                    setHostUploading(true);
                    try { const fd = new FormData(); fd.append("file", f); const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); setHost((h) => ({ ...h, logoFileId: data.file_id })); toast.success("Logo uploaded — Save to apply"); }
                    catch { toast.error("Upload failed"); } finally { setHostUploading(false); }
                  }} />
                </label>
              </div>
              <label className="flex items-center gap-2 text-sm font-[600] text-navy-900 cursor-pointer">
                <input type="checkbox" checked={host.enabled} onChange={(e) => setHost({ ...host, enabled: e.target.checked })} data-testid="host-enabled" className="accent-indigo-600 w-4 h-4" />
                Reserve 2 categories on every zone
              </label>
              <div className="grid grid-cols-2 gap-2">
                {[0, 1].map((i) => (
                  <select key={i} value={host.categoryIds?.[i] || ""} onChange={(e) => setHostCat(i, e.target.value)} data-testid={`host-category-${i}`}
                    className="border border-slate-300 px-2 py-2 text-sm bg-white focus:ring-2 focus:ring-indigo-600 focus:outline-none">
                    <option value="">Category {i + 1}…</option>
                    {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                ))}
              </div>
              <div className="space-y-1.5">
                {[["SPLIT", "1 Front + 1 Back"], ["MERGED_FRONT", "2 Merged Front Slots"]].map(([val, lbl]) => (
                  <label key={val} className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="radio" name="host-placement" checked={host.placement === val} onChange={() => setHost({ ...host, placement: val })} data-testid={`host-placement-${val}`} className="accent-indigo-600" />
                    {lbl}
                  </label>
                ))}
              </div>
              <button onClick={saveHost} data-testid="save-host-btn" className="w-full bg-indigo-600 text-white py-3 font-[600] hover:bg-indigo-700 transition-colors">Save & Apply to All Zones</button>
              <p className="text-[11px] text-slate-400">Reserved blocks are permanently held for the host and excluded from merchant inventory ({cats.length - (host.enabled ? 2 : 0)} open categories per zone).</p>
            </div>
          )}
        </div>
      </div>

      <div className="mt-10">
        <h3 className="font-display text-2xl tracking-tight mb-4">Active Zones</h3>
        <div className="bg-white border border-slate-200 overflow-x-auto" data-testid="zones-table">
          <table className="w-full text-sm">
            <thead className="bg-slate-100 text-left text-xs uppercase tracking-wider text-slate-500">
              <tr><th className="px-6 py-3">Zip</th><th className="px-6 py-3">Sell-through</th><th className="px-6 py-3 text-right">Full Slots (rem/cap)</th><th className="px-6 py-3 text-right">Total</th><th className="px-6 py-3 text-right">Avail</th><th className="px-6 py-3 text-right">Held</th><th className="px-6 py-3 text-right">Sold</th><th className="px-6 py-3 text-right">Actions</th></tr>
            </thead>
            <tbody>
              {zones.length === 0 ? (
                <tr><td colSpan={8} className="px-6 py-10 text-center text-slate-400">No zones generated yet. Add a zip above.</td></tr>
              ) : zones.map((z) => {
                const t = z.total || 1;
                const pct = (n) => `${(n / t) * 100}%`;
                return (
                  <tr key={`${z.zoneId}-${z.zipCode}`} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`zone-row-${z.zipCode}`}>
                    <td className="px-6 py-4 font-[600] text-navy-900">{z.zipCode}<span className="block text-[10px] text-slate-400 font-normal">{z.zoneId}</span></td>
                    <td className="px-6 py-4">
                      <div className="h-2.5 w-40 bg-slate-100 flex overflow-hidden rounded-full" data-testid={`zone-heatmap-${z.zipCode}`}>
                        <div style={{ width: pct(z.sold) }} className="bg-navy-900" title={`Sold ${z.sold}`} />
                        <div style={{ width: pct(z.held) }} className="bg-amber-500" title={`Held ${z.held}`} />
                        <div style={{ width: pct(z.available) }} className="bg-emerald-500" title={`Available ${z.available}`} />
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right tabular-nums font-[600] text-navy-900" data-testid={`zone-full-remaining-${z.zipCode}`}>{z.remainingFull}<span className="text-slate-400 font-normal">/{z.capacityFull}</span></td>
                    <td className="px-6 py-4 text-right tabular-nums">{z.total}</td>
                    <td className="px-6 py-4 text-right tabular-nums text-emerald-700 font-[600]">{z.available}</td>
                    <td className="px-6 py-4 text-right tabular-nums text-amber-600">{z.held}</td>
                    <td className="px-6 py-4 text-right tabular-nums text-navy-900 font-[600]">{z.sold}</td>
                    <td className="px-6 py-4 text-right"><button onClick={() => removeZone(z)} data-testid={`zone-delete-${z.zipCode}`} className="text-xs font-[600] text-red-600 hover:underline inline-flex items-center gap-1"><Trash size={13} /> Delete</button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function SalesReps() {
  const [reps, setReps] = useState([]);
  const [form, setForm] = useState({ fullName: "", email: "", phone: "", password: "", zips: "" });
  const [creating, setCreating] = useState(false);

  const load = () => api.get("/admin/sales-reps").then(({ data }) => setReps(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!form.fullName || !form.email || !form.password) { toast.error("Name, email and password are required"); return; }
    if (form.password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setCreating(true);
    try {
      const assignedZipCodes = form.zips.split(",").map((z) => z.trim()).filter(Boolean);
      await api.post("/admin/sales-reps", { fullName: form.fullName, email: form.email, phone: form.phone, password: form.password, assignedZipCodes });
      toast.success(`Sales rep ${form.fullName} created`);
      setForm({ fullName: "", email: "", phone: "", password: "", zips: "" });
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setCreating(false); }
  };
  const toggleStatus = async (r) => {
    const status = r.status === "ACTIVE" ? "INACTIVE" : "ACTIVE";
    try { await api.patch(`/admin/sales-reps/${r.repId}`, { status }); toast.success(`Rep ${status === "ACTIVE" ? "activated" : "deactivated"}`); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <div>
      <SectionHead eyebrow="Field Team" title="Sales Reps" />
      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 bg-white border border-slate-200 overflow-x-auto" data-testid="sales-reps-table">
          <table className="w-full text-sm">
            <thead className="bg-slate-100 text-left text-xs uppercase tracking-wider text-slate-500">
              <tr><th className="px-6 py-3">Rep</th><th className="px-6 py-3">Territory Zips</th><th className="px-6 py-3">Referral</th><th className="px-6 py-3">Status</th><th className="px-6 py-3 text-right">Actions</th></tr>
            </thead>
            <tbody>
              {reps.length === 0 ? (
                <tr><td colSpan={5} className="px-6 py-10 text-center text-slate-400">No sales reps yet. Create one to onboard your field team.</td></tr>
              ) : reps.map((r) => (
                <tr key={r.repId} className="border-t border-slate-100" data-testid={`rep-row-${r.repId}`}>
                  <td className="px-6 py-4"><span className="font-[600] text-navy-900">{r.fullName}</span><span className="block text-[11px] text-slate-400">{r.email}</span></td>
                  <td className="px-6 py-4 text-slate-500">{(r.assignedZipCodes || []).join(", ") || "—"}</td>
                  <td className="px-6 py-4 text-slate-500 font-mono text-xs">{r.referralCode}</td>
                  <td className="px-6 py-4"><span className={`text-xs font-bold uppercase ${r.status === "ACTIVE" ? "text-emerald-700" : "text-slate-400"}`}>{r.status}</span></td>
                  <td className="px-6 py-4 text-right"><button onClick={() => toggleStatus(r)} data-testid={`rep-toggle-${r.repId}`} className="text-xs font-[600] text-navy-900 hover:underline">{r.status === "ACTIVE" ? "Deactivate" : "Activate"}</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <form onSubmit={create} className="bg-white border border-slate-200 p-6 space-y-3 h-fit" data-testid="new-rep-form">
          <h3 className="font-display text-xl tracking-tight flex items-center gap-2"><Plus size={18} /> New Sales Rep</h3>
          <input required placeholder="Full name" value={form.fullName} onChange={(e) => setForm({ ...form, fullName: e.target.value })} data-testid="rep-name" className="w-full border border-slate-300 px-3 py-2.5 text-sm focus:ring-2 focus:ring-navy-900 focus:outline-none" />
          <input required type="email" placeholder="Email (login)" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="rep-email" className="w-full border border-slate-300 px-3 py-2.5 text-sm focus:ring-2 focus:ring-navy-900 focus:outline-none" />
          <input placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="rep-phone" className="w-full border border-slate-300 px-3 py-2.5 text-sm focus:ring-2 focus:ring-navy-900 focus:outline-none" />
          <input required type="password" placeholder="Temp password (min 6)" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="rep-password" className="w-full border border-slate-300 px-3 py-2.5 text-sm focus:ring-2 focus:ring-navy-900 focus:outline-none" />
          <input placeholder="Territory zips (comma-separated)" value={form.zips} onChange={(e) => setForm({ ...form, zips: e.target.value })} data-testid="rep-zips" className="w-full border border-slate-300 px-3 py-2.5 text-sm focus:ring-2 focus:ring-navy-900 focus:outline-none" />
          <button type="submit" disabled={creating} data-testid="create-rep-btn" className="w-full bg-navy-900 text-white py-3 font-[600] hover:bg-emerald-700 transition-colors disabled:opacity-60">{creating ? "Creating…" : "Create Sales Rep"}</button>
          <p className="text-[11px] text-slate-400">Rep logs in at /login and lands on their Sales Dashboard.</p>
        </form>
      </div>
    </div>
  );
}


function CreativeReview() {
  const [items, setItems] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState("");
  const [notes, setNotes] = useState({});
  const backend = process.env.REACT_APP_BACKEND_URL;
  const abs = (u) => (u ? `${u.startsWith("http") ? "" : backend}${u}` : null);

  const load = () => {
    setLoading(true);
    api.get("/admin/artwork-pending").then(({ data }) => setItems(data)).catch(() => {}).finally(() => setLoading(false));
    api.get("/admin/artwork-log").then(({ data }) => setLogs(data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const review = async (it, action) => {
    const key = `${it.zipCode}:${it.categoryId}`;
    setActing(`${key}:${action}`);
    try {
      await api.patch("/admin/artwork-review", { zipCode: it.zipCode, categoryId: it.categoryId, action, note: action === "REJECT" ? (notes[key] || "") : undefined });
      toast.success(action === "APPROVE" ? "Artwork approved — merchant notified" : "Artwork rejected — merchant will see your note");
      setNotes((n) => { const c = { ...n }; delete c[key]; return c; });
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setActing(""); }
  };

  return (
    <div data-testid="admin-creative">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-3xl tracking-tight">Creative Approval Queue</h1>
          <p className="text-sm text-slate-500 mt-1">Merchant-submitted artwork awaiting review. Approved designs appear on the live mailer.</p>
        </div>
        <button onClick={load} data-testid="creative-refresh" className="flex items-center gap-2 border border-slate-300 px-4 py-2 text-sm font-[600] hover:border-navy-900 transition-colors"><ArrowClockwise size={16} /> Refresh</button>
      </div>

      {loading ? <p className="text-slate-400">Loading…</p> : items.length === 0 ? (
        <div className="bg-white border border-slate-200 p-12 text-center text-slate-400" data-testid="creative-empty">
          <Clock size={40} className="mx-auto mb-3 text-slate-300" />
          No pending artwork. Merchant uploads will show here for approval.
        </div>
      ) : (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-5">
          {items.map((it) => (
            <div key={`${it.zipCode}-${it.categoryId}`} data-testid={`pending-${it.zipCode}-${it.categoryId}`} className="bg-white border border-slate-200 p-5">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="font-display text-lg tracking-tight text-navy-900">{it.name}</p>
                  <p className="text-[11px] uppercase tracking-wider text-slate-400">Zip {it.zipCode}{it.business ? ` · ${it.business}` : ""}</p>
                </div>
                <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 bg-amber-100 text-amber-700">Pending</span>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-2">
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">New submission</p>
                  <div className="border border-amber-300 bg-slate-50 aspect-video flex items-center justify-center overflow-hidden">
                    {abs(it.pendingArtworkUrl) ? <img src={abs(it.pendingArtworkUrl)} alt="pending" className="max-h-full max-w-full object-contain" /> : <span className="text-xs text-slate-400">—</span>}
                  </div>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">Current live</p>
                  <div className="border border-slate-200 bg-slate-50 aspect-video flex items-center justify-center overflow-hidden">
                    {abs(it.liveArtworkUrl) ? <img src={abs(it.liveArtworkUrl)} alt="live" className="max-h-full max-w-full object-contain" /> : <span className="text-xs text-slate-400">None</span>}
                  </div>
                </div>
              </div>
              <div className="mt-4">
                <textarea value={notes[`${it.zipCode}:${it.categoryId}`] || ""}
                  onChange={(e) => setNotes((n) => ({ ...n, [`${it.zipCode}:${it.categoryId}`]: e.target.value }))}
                  data-testid={`reject-note-${it.zipCode}-${it.categoryId}`} rows={2}
                  placeholder="Optional note if rejecting (e.g. low resolution, wrong dimensions)…"
                  className="w-full border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-navy-900 focus:outline-none" />
              </div>
              <div className="flex gap-2 mt-3">
                <button onClick={() => review(it, "APPROVE")} disabled={!!acting} data-testid={`approve-${it.zipCode}-${it.categoryId}`}
                  className="flex-1 bg-emerald-700 text-white py-2.5 text-sm font-[600] hover:bg-emerald-800 transition-colors disabled:opacity-50">
                  {acting === `${it.zipCode}:${it.categoryId}:APPROVE` ? "…" : "Approve"}
                </button>
                <button onClick={() => review(it, "REJECT")} disabled={!!acting} data-testid={`reject-${it.zipCode}-${it.categoryId}`}
                  className="flex-1 border border-red-300 text-red-600 py-2.5 text-sm font-[600] hover:bg-red-50 transition-colors disabled:opacity-50">
                  {acting === `${it.zipCode}:${it.categoryId}:REJECT` ? "…" : "Reject"}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="mt-10" data-testid="artwork-log">
        <h2 className="font-display text-2xl tracking-tight">Approval History</h2>
        <p className="text-sm text-slate-500 mt-1">Audit trail of every approve/reject decision.</p>
        {logs.length === 0 ? (
          <p className="text-slate-400 mt-4 text-sm" data-testid="artwork-log-empty">No decisions logged yet.</p>
        ) : (
          <div className="mt-4 bg-white border border-slate-200 divide-y divide-slate-100">
            {logs.map((l) => (
              <div key={l.id} data-testid={`log-${l.id}`} className="flex items-center gap-4 px-4 py-3 text-sm">
                <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 shrink-0 ${l.action === "APPROVE" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>{l.action === "APPROVE" ? "Approved" : "Rejected"}</span>
                <div className="flex-1 min-w-0">
                  <p className="font-[600] text-navy-900 truncate">{l.categoryName} <span className="text-slate-400 font-normal">· Zip {l.zipCode}</span></p>
                  {l.note && <p className="text-[12px] text-slate-500 truncate">“{l.note}”</p>}
                </div>
                <div className="text-right shrink-0">
                  <p className="text-[12px] text-slate-600">{l.by}</p>
                  <p className="text-[11px] text-slate-400">{l.at ? new Date(l.at).toLocaleString() : ""}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function AdminMailer() {
  const [camps, setCamps] = useState([]);
  const [sel, setSel] = useState("");
  const [form, setForm] = useState({ city: "", state: "", label: "", zipCodes: "", primaryZip: "" });
  const [saving, setSaving] = useState(false);
  const load = () => api.get("/mailer-campaigns").then(({ data }) => { setCamps(data); setSel((s) => s || data[0]?.id || ""); }).catch(() => {});
  useEffect(() => { load(); const t = setInterval(load, 7000); return () => clearInterval(t); }, []);
  const camp = camps.find((c) => c.id === sel) || camps[0];

  const create = async () => {
    const zips = form.zipCodes.split(",").map((z) => z.trim()).filter(Boolean);
    if (!form.city.trim() || !zips.length) { toast.error("City and at least one ZIP required"); return; }
    setSaving(true);
    try {
      await api.post("/admin/mailer-campaigns", { city: form.city, state: form.state, label: form.label, zipCodes: zips, primaryZip: form.primaryZip || zips[0] });
      toast.success("Campaign created"); setForm({ city: "", state: "", label: "", zipCodes: "", primaryZip: "" }); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const remove = async (id) => { try { await api.delete(`/admin/mailer-campaigns/${id}`); toast.success("Campaign removed"); load(); } catch { toast.error("Could not remove"); } };

  return (
    <div data-testid="admin-mailer">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-display text-3xl tracking-tight">Live Mailer Canvas</h1>
          <p className="text-sm text-slate-500 mt-1">City + multi-ZIP campaigns, shared across Sales & Partner portals (~7s real-time sync).</p>
        </div>
        <select value={sel} onChange={(e) => setSel(e.target.value)} data-testid="admin-mailer-zip"
          className="border border-slate-300 px-4 py-2.5 bg-white focus:ring-2 focus:ring-navy-900 focus:outline-none max-w-[360px]">
          {camps.map((c) => <option key={c.id} value={c.id}>{c.descriptor}</option>)}
        </select>
      </div>

      <div className="mb-6 bg-white border border-slate-200 p-5" data-testid="campaign-create">
        <p className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-3">New City + Multi-ZIP Campaign</p>
        <div className="grid sm:grid-cols-5 gap-2">
          <input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="City (Orlando)" data-testid="camp-city" className="border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-navy-900 focus:outline-none" />
          <input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} placeholder="State (FL)" data-testid="camp-state" className="border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-navy-900 focus:outline-none" />
          <input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="Label (Winter Garden)" data-testid="camp-label" className="border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-navy-900 focus:outline-none" />
          <input value={form.zipCodes} onChange={(e) => setForm({ ...form, zipCodes: e.target.value })} placeholder="ZIPs (34787, 34786)" data-testid="camp-zips" className="border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-navy-900 focus:outline-none" />
          <button onClick={create} disabled={saving} data-testid="camp-create-btn" className="bg-navy-900 text-white px-4 py-2 text-sm font-[600] hover:bg-emerald-700 transition-colors disabled:opacity-50">{saving ? "Saving…" : "Create"}</button>
        </div>
        {camps.filter((c) => !c.id.startsWith("zip-")).length > 0 && (
          <ul className="mt-4 space-y-1.5" data-testid="campaign-list">
            {camps.filter((c) => !c.id.startsWith("zip-")).map((c) => (
              <li key={c.id} className="flex items-center justify-between text-sm border-b border-slate-100 pb-1.5">
                <span className="text-navy-900">{c.descriptor} <em className="not-italic text-slate-400">· primary {c.primaryZip}</em></span>
                <button onClick={() => remove(c.id)} data-testid={`camp-del-${c.id}`} className="text-xs text-slate-400 hover:text-red-600">Remove</button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {camp?.zone
        ? <PostcardPreview zone={camp.zone} title="9×12 Mailer · Live (Admin View)" generic />
        : <div className="bg-white border border-slate-200 p-12 text-center text-slate-400">No active mailers yet.</div>}
    </div>
  );
}
